/**
 * AccessibilityContext — React Context for end-user accessibility settings.
 *
 * Provides centralized state for theme override, font scale, animation
 * toggle, high contrast mode, line spacing, CVD mode, font family, and
 * font size. State is scoped by agentId and persisted to localStorage so
 * multiple widget instances don't interfere.
 *
 * @module contexts/AccessibilityContext
 * @see TASK-WIDGET-ACCESSIBILITY-CONTEXT
 */

import React, { createContext, useState, useEffect, useCallback } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** User-selectable theme names. 'original' preserves the admin-configured colors. */
export type AccessibilityTheme =
  | 'original'
  | 'light'
  | 'dark'
  | 'high-contrast'
  | 'sepia';

/** Color-vision deficiency simulation mode. */
export type CvdMode = 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia' | 'achromatopsia';

/** Reduced font-family choices for end-user accessibility. */
export type AccessibilityFontFamily = 'system' | 'serif' | 'monospace';

/** Allowed line-spacing multipliers. */
export type LineSpacing = 1.2 | 1.5 | 1.8 | 2.0;

/** Valid line-spacing values for runtime validation. */
const VALID_LINE_SPACINGS: readonly number[] = [1.2, 1.5, 1.8, 2.0];

/** Valid font-family values for runtime validation. */
const VALID_FONT_FAMILIES: readonly string[] = ['system', 'serif', 'monospace'];

/** Valid CVD mode values for runtime validation. */
const VALID_CVD_MODES: readonly string[] = ['none', 'protanopia', 'deuteranopia', 'tritanopia', 'achromatopsia'];

/** Font-family → CSS font stack mapping. */
const FONT_FAMILY_STACKS: Record<AccessibilityFontFamily, string> = {
  system: 'system-ui, -apple-system, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
  monospace: '"Fira Code", "Consolas", monospace',
};

/** Persisted settings shape. */
export interface AccessibilitySettings {
  theme: AccessibilityTheme;
  /**
   * Font scale multiplier (0.8 – 1.4). Consumed by the widget container, which
   * declares `--widget-font-scale` on ITSELF and applies the zoom
   * (ChatWidget.tsx). It is deliberately NOT written to
   * document.documentElement: the governing criterion scopes the property to
   * the widget container, and in an embedded widget the document root belongs
   * to a third-party host page.
   */
  fontScale: number;
  animationsEnabled: boolean;
  /** WCAG AAA high contrast mode. */
  highContrast: boolean;
  /** Line height multiplier. */
  lineSpacing: LineSpacing;
  /** Color-vision deficiency simulation mode. */
  cvdMode: CvdMode;
  /** End-user font family override. */
  fontFamily: AccessibilityFontFamily;
  /** Font size in rem (0.875 – 1.5). */
  fontSize: number;
}

/** Full context value exposed to consumers. */
export interface AccessibilityContextValue extends AccessibilitySettings {
  /** Browser-level reduced-motion preference (read-only). */
  prefersReducedMotion: boolean;
  /** Browser-level high-contrast preference (read-only). */
  prefersHighContrast: boolean;
  /** Partial update — merges provided fields into current settings. */
  updateSettings: (updates: Partial<AccessibilitySettings>) => void;
  /** Reset to defaults (clears localStorage). */
  resetSettings: () => void;
}

export interface AccessibilityProviderProps {
  /** Agent ID for per-widget scoping of localStorage keys. */
  agentId: string;
  children: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULT_FONT_SCALE = 1;
const DEFAULT_FONT_SIZE = 1;
const FONT_SIZE_MIN = 0.875;
const FONT_SIZE_MAX = 1.5;

function getDefaults(
  prefersReducedMotion: boolean,
  prefersHighContrast: boolean,
): AccessibilitySettings {
  return {
    theme: 'original',
    fontScale: DEFAULT_FONT_SCALE,
    animationsEnabled: !prefersReducedMotion,
    highContrast: prefersHighContrast,
    lineSpacing: 1.5,
    cvdMode: 'none',
    fontFamily: 'system',
    fontSize: DEFAULT_FONT_SIZE,
  };
}

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

function storageKey(agentId: string): string {
  return `verisa-accessibility-${agentId}`;
}

function loadSettings(
  agentId: string,
  prefersReducedMotion: boolean,
  prefersHighContrast: boolean,
): AccessibilitySettings {
  const defaults = getDefaults(prefersReducedMotion, prefersHighContrast);
  try {
    const raw = localStorage.getItem(storageKey(agentId));
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<AccessibilitySettings>;
    return {
      theme: parsed.theme ?? defaults.theme,
      fontScale: typeof parsed.fontScale === 'number'
        ? Math.min(1.4, Math.max(0.8, parsed.fontScale))
        : defaults.fontScale,
      animationsEnabled: typeof parsed.animationsEnabled === 'boolean'
        ? parsed.animationsEnabled
        : defaults.animationsEnabled,
      highContrast: typeof parsed.highContrast === 'boolean'
        ? parsed.highContrast
        : defaults.highContrast,
      lineSpacing: VALID_LINE_SPACINGS.includes(parsed.lineSpacing as number)
        ? (parsed.lineSpacing as LineSpacing)
        : defaults.lineSpacing,
      cvdMode: VALID_CVD_MODES.includes(parsed.cvdMode as string)
        ? (parsed.cvdMode as CvdMode)
        : defaults.cvdMode,
      fontFamily: VALID_FONT_FAMILIES.includes(parsed.fontFamily as string)
        ? (parsed.fontFamily as AccessibilityFontFamily)
        : defaults.fontFamily,
      fontSize: typeof parsed.fontSize === 'number'
        ? Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, parsed.fontSize))
        : defaults.fontSize,
    };
  } catch {
    return defaults;
  }
}

function saveSettings(agentId: string, settings: AccessibilitySettings): void {
  try {
    localStorage.setItem(storageKey(agentId), JSON.stringify(settings));
  } catch {
    // DOMException in sandboxed iframes without allow-same-origin, private browsing, or quota exceeded
  }
}

function clearSettings(agentId: string): void {
  try {
    localStorage.removeItem(storageKey(agentId));
  } catch {
    // DOMException in sandboxed iframes without allow-same-origin
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AccessibilityContext = createContext<AccessibilityContextValue | undefined>(undefined);

export { AccessibilityContext };

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({
  agentId,
  children,
}) => {
  // Detect browser reduced-motion preference
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  // Detect browser high-contrast preference
  const [prefersHighContrast, setPrefersHighContrast] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-contrast: more)').matches;
  });

  const [settings, setSettings] = useState<AccessibilitySettings>(() =>
    loadSettings(agentId, prefersReducedMotion, prefersHighContrast),
  );

  // Listen for prefers-reduced-motion changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');

    const handler = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
      if (e.matches) {
        setSettings((prev) => {
          const next = { ...prev, animationsEnabled: false };
          saveSettings(agentId, next);
          return next;
        });
      }
    };

    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [agentId]);

  // Listen for prefers-contrast changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-contrast: more)');

    const handler = (e: MediaQueryListEvent) => {
      setPrefersHighContrast(e.matches);
      if (e.matches) {
        setSettings((prev) => {
          const next = { ...prev, highContrast: true };
          saveSettings(agentId, next);
          return next;
        });
      }
    };

    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [agentId]);

  // Apply --verisa-line-spacing whenever lineSpacing changes
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--verisa-line-spacing',
      String(settings.lineSpacing),
    );
  }, [settings.lineSpacing]);

  // Apply --verisa-a11y-font-size whenever fontSize changes
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--verisa-a11y-font-size',
      `${settings.fontSize}rem`,
    );
  }, [settings.fontSize]);

  // Apply --verisa-a11y-font-family whenever fontFamily changes
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--verisa-a11y-font-family',
      FONT_FAMILY_STACKS[settings.fontFamily],
    );
  }, [settings.fontFamily]);

  // Toggle verisa-high-contrast class on documentElement
  useEffect(() => {
    const el = document.documentElement;
    if (settings.highContrast) {
      el.classList.add('verisa-high-contrast');
    } else {
      el.classList.remove('verisa-high-contrast');
    }
  }, [settings.highContrast]);

  const updateSettings = useCallback(
    (updates: Partial<AccessibilitySettings>) => {
      setSettings((prev) => {
        const next: AccessibilitySettings = { ...prev, ...updates };
        // Clamp fontScale
        if (typeof next.fontScale === 'number') {
          next.fontScale = Math.min(1.4, Math.max(0.8, next.fontScale));
        }
        // Clamp fontSize
        if (typeof next.fontSize === 'number') {
          next.fontSize = Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, next.fontSize));
        }
        saveSettings(agentId, next);
        return next;
      });
    },
    [agentId],
  );

  const resetSettings = useCallback(() => {
    clearSettings(agentId);
    const defaults = getDefaults(prefersReducedMotion, prefersHighContrast);
    setSettings(defaults);
  }, [agentId, prefersReducedMotion, prefersHighContrast]);

  const value: AccessibilityContextValue = {
    ...settings,
    prefersReducedMotion,
    prefersHighContrast,
    updateSettings,
    resetSettings,
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export default AccessibilityProvider;
